import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AgendaProject {
  id: number;
  user_id: number;
  owner_id?: number | null;
  owner_name?: string | null;
  google_event_id?: string | null;
  title?: string | null;
  /** Objet du RDV (texte libre saisi dans l'agenda). */
  description?: string | null;
  /** Commentaire de la Transaction liée (même valeur qu'en historique / transactions). */
  commentaire?: string | null;
  /** Phase du pipe de la transaction liée (lecture seule). */
  phase?: string | null;
  /** Résultat d'appel de la transaction liée : OK | NRP, null = jamais appelé. */
  resultat_appel?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  civilite?: string | null;
  nom?: string | null;
  prenom?: string | null;
  telephone?: string | null;
  email?: string | null;
  status: string;
  statut_lead?: string | null;
  archived_at?: string | null;
  is_archived?: boolean;
  // Renseigné = RDV remis en actif à la main : l'archivage automatique des
  // RDV passés (24 h) ne le reprendra plus.
  unarchived_at?: string | null;
  nom_vendeur?: string | null;
  origine?: string | null;
  project_id?: string | null;
  raw_payload?: any;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PaginatedAgendaProjectsResponse {
  items: AgendaProject[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AgendaProjectCreateResponse extends AgendaProject {}

export interface AgendaProjectPrefill {
  agendaProjectId: number;
  civilite?: string | null;
  nom?: string | null;
  prenom?: string | null;
  telephone?: string | null;
  email?: string | null;
  origine?: string | null;
  commentaireEmprunteur?: string | null;
  nomProjet?: string | null;
  pays?: string | null;
}

export interface AgendaProjectConversionResponse {
  // 'prefill' : aucun projet créé, le formulaire est pré-rempli puis la
  // comparaison crée le ProjectHistory/Contact. 'load_existing' : un projet a
  // déjà été créé lors d'une comparaison précédente, on le recharge.
  mode: 'prefill' | 'load_existing';
  message?: string;
  agenda_project: AgendaProject;
  project_id: string | null;
  prefill: AgendaProjectPrefill | null;
}

@Injectable({
  providedIn: 'root'
})
export class AgendaProjectsService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/agenda-projects`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  getAgendaProjects(limit: number = 10, offset: number = 0, filters?: { search?: string; status?: string; statut_lead?: string; today_only?: boolean; user_id?: number; archived?: boolean }): Observable<PaginatedAgendaProjectsResponse> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (filters?.search) {
      params = params.set('search', filters.search);
    }

    if (filters?.status) {
      params = params.set('status', filters.status);
    }

    if (filters?.statut_lead) {
      params = params.set('statut_lead', filters.statut_lead);
    }

    if (filters?.today_only) {
      params = params.set('today_only', 'true');
    }

    if (filters?.user_id != null) {
      params = params.set('user_id', filters.user_id.toString());
    }

    if (filters?.archived) {
      params = params.set('archived', 'true');
    }

    return this.http.get<PaginatedAgendaProjectsResponse>(this.apiUrl, {
      params,
      headers: this.getAuthHeaders()
    });
  }

  getAgendaProject(agendaProjectId: number): Observable<AgendaProject> {
    return this.http.get<AgendaProject>(
      `${this.apiUrl}/${agendaProjectId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  updateAgendaProject(agendaProjectId: number, data: Partial<AgendaProject> & { archived?: boolean }): Observable<AgendaProject> {
    return this.http.patch<AgendaProject>(
      `${this.apiUrl}/${agendaProjectId}`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  createProjectFromAgenda(agendaProjectId: number): Observable<AgendaProjectConversionResponse> {
    return this.http.post<AgendaProjectConversionResponse>(
      `${this.apiUrl}/${agendaProjectId}/create-project`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  deleteAgendaProject(agendaProjectId: number): Observable<{ message: string; id: number }> {
    return this.http.delete<{ message: string; id: number }>(
      `${this.apiUrl}/${agendaProjectId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  exportAgendaProjectsExcel(
    agendaProjectIds: number[] = [],
    options: { export_all_matching?: boolean; search?: string; status?: string; statut_lead?: string; today_only?: boolean; user_id?: number; archived?: boolean } = {}
  ): Observable<HttpResponse<Blob>> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post(`${this.apiUrl}/export/excel`, {
      agenda_project_ids: agendaProjectIds,
      ...options
    }, {
      headers,
      observe: 'response',
      responseType: 'blob'
    });
  }
}
